import { useAuth } from "@Auth/useAuth";
import {
  CustomersClient,
  CustomerType,
  AddressType,
  ICustomerAddressWithId,
  ICustomerAddress,
} from "@Clients/CustomersClient";
import { Footer } from "@common/SimpleDrawer/components/Footer";
import { Section } from "@common/SimpleDrawer/components/Section";
import { FormInput } from "@components/FormInput/FormInput";
import { FormAutocomplete } from "@components/FormAutocomplete/FormAutoComplete";
import { DRAWER_CONTEXT } from "@models/DrawerContext";
import { Entity } from "@models/Entity";
import { FORM_ACTIONS } from "@models/FormActions";
import { Box, Button, IconButton, Typography, Chip } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useConfirmationModalStore } from "@stores/confirmationModalStore";
import { useCustomersStore } from "@stores/customersStore";
import { useDrawer } from "@hooks/useDrawer";
import { onErrorShow } from "@utils/onErrorShow";
import { onInfoShow } from "@utils/onInfoShow";
import { returnErrorMessage } from "@utils/returnErrorMessage";
import { useEffect, useReducer } from "react";

// Use shared types from CustomersClient
type CustomerAddress = ICustomerAddressWithId;
type CustomerAddressWithoutId = ICustomerAddress;

type CustomerInitialState = {
  name: string;
  email: string;
  phone: string;
  customer_type: CustomerType;
  external_id: string;
  status: "active" | "inactive" | "archived";
  addresses: CustomerAddress[];
  errors: Record<keyof Omit<CustomerInitialState, "errors">, string>;
};

const initialState: CustomerInitialState = {
  name: "",
  email: "",
  phone: "",
  customer_type: null,
  external_id: "",
  status: "active",
  addresses: [],
  errors: {} as Record<keyof Omit<CustomerInitialState, "errors">, string>,
};

interface Action<T> {
  type: FORM_ACTIONS;
  field?: keyof T;
  value?: string | CustomerType | CustomerAddress[];
  addressId?: string;
  addressField?: keyof CustomerAddress;
  addressValue?: string | boolean;
  errors?: Record<keyof T, string>;
}

function formReducer(
  state: CustomerInitialState,
  action: Action<CustomerInitialState>
) {
  switch (action.type) {
    case FORM_ACTIONS.SET_FIELD: {
      if (!action.field) return state;

      // Handle address-specific actions
      if (
        action.field === "addresses" &&
        action.addressId &&
        action.addressField
      ) {
        const updatedAddresses = (state.addresses || []).map((addr) =>
          addr.id === action.addressId
            ? { ...addr, [action.addressField!]: action.addressValue }
            : addr
        );
        return {
          ...state,
          addresses: updatedAddresses,
        };
      }

      return {
        ...state,
        [action.field!]: action.value,
        errors: {
          ...state.errors,
          [action.field!]: "",
        },
      };
    }
    case FORM_ACTIONS.SET_ERRORS:
      return {
        ...state,
        errors: action.errors || {},
      };
    case FORM_ACTIONS.RESET:
      return initialState;
    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
}

type CustomerFormProps = {
  handleClose: () => void;
  onCustomerCreated?: (customerId: string) => void;
  createFromOrderView?: boolean;
};

const customerTypeOptions = [
  { label: "Individual", value: "individual" },
  { label: "Business", value: "business" },
  { label: "Wholesale", value: "wholesale" },
];

const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Archived", value: "archived" },
];

const addressTypeOptions = [
  { label: "Shipping", value: "shipping" },
  { label: "Billing", value: "billing" },
  { label: "Delivery", value: "delivery" },
  { label: "Other", value: "other" },
];

export const CustomerForm = ({
  handleClose,
  onCustomerCreated,
  createFromOrderView,
}: CustomerFormProps) => {
  const {
    context,
    selectedId,
    close: closeOnSubmit,
  } = useDrawer();

  const { baseUser } = useAuth();
  const { getCustomersByOrganizationId, customers } = useCustomersStore();
  const { setTouch } = useConfirmationModalStore();

  const [state, dispatch] = useReducer(
    formReducer as React.Reducer<
      Partial<CustomerInitialState>,
      Action<CustomerInitialState>
    >,
    initialState
  );

  useEffect(() => {
    if (context === DRAWER_CONTEXT.EDIT_CUSTOMER && selectedId) {
      const customer = customers.find((customer) => customer.id === selectedId);
      if (customer) {
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "name",
          value: customer.name,
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "email",
          value: customer.email || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "phone",
          value: customer.phone || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "customer_type",
          value: customer.customer_type || null,
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "external_id",
          value: customer.external_id || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "status",
          value: customer.status,
        });

        // Load addresses from metadata
        const metadataAddresses = customer.metadata?.addresses;
        if (metadataAddresses && Array.isArray(metadataAddresses)) {
          // Add temporary IDs to addresses loaded from metadata
          const addressesWithIds: CustomerAddress[] = metadataAddresses.map(
            (addr: Omit<CustomerAddress, "id">, index: number) => ({
              ...addr,
              id: `addr-${Date.now()}-${index}`,
            })
          );
          dispatch({
            type: FORM_ACTIONS.SET_FIELD,
            field: "addresses",
            value: addressesWithIds,
          });
        }
      }
    }
  }, [context, selectedId, customers]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: e.target.name as keyof CustomerInitialState,
      value: e.target.value,
    });
    setTouch(true);
  };

  const handleAddAddress = () => {
    const newAddress: CustomerAddress = {
      id: `addr-${Date.now()}`,
      type: "shipping",
      street: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
      is_default: (state.addresses || []).length === 0, // First address is default
    };
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "addresses",
      value: [...(state.addresses || []), newAddress],
    });
    setTouch(true);
  };

  const handleRemoveAddress = (addressId: string) => {
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "addresses",
      value: (state.addresses || []).filter((addr) => addr.id !== addressId),
    });
    setTouch(true);
  };

  const handleAddressChange = (
    addressId: string,
    field: keyof CustomerAddress,
    value: string | boolean
  ) => {
    // If setting is_default to true, unset all other addresses
    if (field === "is_default" && value === true) {
      const updatedAddresses = (state.addresses || []).map((addr) => ({
        ...addr,
        is_default: addr.id === addressId,
      }));
      dispatch({
        type: FORM_ACTIONS.SET_FIELD,
        field: "addresses",
        value: updatedAddresses,
      });
    } else {
      dispatch({
        type: FORM_ACTIONS.SET_FIELD,
        field: "addresses",
        addressId,
        addressField: field,
        addressValue: value,
      });
    }
    setTouch(true);
  };

  const handleFormVerification = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    const errors = {} as Record<
      keyof Omit<CustomerInitialState, "errors">,
      string
    >;

    const requiredFields: (keyof Omit<CustomerInitialState, "errors">)[] = [
      "name",
    ];

    requiredFields.forEach((field) => {
      if (!state[field]) {
        errors[field] = `${field} is required`;
      }
    });

    // Validate email format if provided
    if (state.email && state.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(state.email)) {
        errors.email = "Invalid email format";
      }
    }

    if (Object.keys(errors).length > 0) {
      dispatch({
        type: FORM_ACTIONS.SET_ERRORS,
        errors: errors as Record<keyof CustomerInitialState, string>,
      });
      return;
    }
    return true;
  };

  const handleCreateFromOrderWindow = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const validForm = handleFormVerification(e);

    if (!validForm) return;

    const {
      name,
      email,
      phone,
      customer_type,
      external_id,
      status,
      addresses,
    } = state;

    // Prepare metadata with addresses
    const metadata: Record<
      string,
      string | number | boolean | null | undefined | CustomerAddressWithoutId[]
    > = {};
    if (addresses && addresses.length > 0) {
      // Filter out empty addresses and remove the id field before saving
      const validAddresses: CustomerAddressWithoutId[] = addresses
        .filter(
          (addr) =>
            addr.street?.trim() ||
            addr.city?.trim() ||
            addr.state?.trim() ||
            addr.postal_code?.trim() ||
            addr.country?.trim()
        )
        .map(({ ...addr }) => addr);
      if (validAddresses.length > 0) {
        metadata.addresses = validAddresses;
      }
    }

    try {
      const newCustomer = await CustomersClient.createCustomer({
        name: name!,
        email: email?.trim() || null,
        phone: phone?.trim() || null,
        customer_type: customer_type || null,
        external_id: external_id?.trim() || null,
        status: status || "active",
        organization_id: baseUser!.organization_id!,
        metadata:
          Object.keys(metadata).length > 0
            ? (metadata as Record<
                string,
                string | number | boolean | null | undefined
              >)
            : undefined,
      });

      onInfoShow("Customer created successfully");
      getCustomersByOrganizationId(baseUser!.organization_id!, false);
      // Call callback to auto-select new customer in order form
      if (onCustomerCreated) {
        onCustomerCreated(newCustomer.id);
      }
      handleClose();
      setTouch(false);
    } catch (error: unknown) {
      if (error instanceof Error && error.message) {
        const errorMessage = returnErrorMessage(
          Entity.CUSTOMERS,
          error.message
        );
        onErrorShow(errorMessage || "Failed to save customer");
        return;
      }
      onErrorShow("Failed to save customer");
    }
  };

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      const validForm = handleFormVerification(e);

      if (!validForm) return;

      const {
        name,
        email,
        phone,
        customer_type,
        external_id,
        status,
        addresses,
      } = state;

      // Prepare metadata with addresses
      const metadata: Record<
        string,
        | string
        | number
        | boolean
        | null
        | undefined
        | CustomerAddressWithoutId[]
      > = {};
      if (addresses && addresses.length > 0) {
        // Filter out empty addresses and remove the id field before saving
        const validAddresses: CustomerAddressWithoutId[] = addresses
          .filter(
            (addr) =>
              addr.street.trim() ||
              addr.city.trim() ||
              addr.state.trim() ||
              addr.postal_code.trim() ||
              addr.country.trim()
          )
          .map(({ ...addr }) => addr);
        if (validAddresses.length > 0) {
          metadata.addresses = validAddresses;
        }
      }

      if (context === DRAWER_CONTEXT.EDIT_CUSTOMER && selectedId) {
        // **Update existing customer**
        await CustomersClient.updateCustomer({
          id: selectedId,
          name: name!,
          email: email?.trim() || null,
          phone: phone?.trim() || null,
          customer_type: customer_type || null,
          external_id: external_id?.trim() || null,
          status: status || "active",
          organization_id: baseUser!.organization_id!,
          metadata:
            Object.keys(metadata).length > 0
              ? (metadata as Record<
                  string,
                  string | number | boolean | null | undefined
                >)
              : undefined,
        });
        closeOnSubmit();
        onInfoShow("Customer updated successfully");
        getCustomersByOrganizationId(baseUser!.organization_id!, false);
      } else {
        // **Create a new customer**
        const newCustomer = await CustomersClient.createCustomer({
          name: name!,
          email: email?.trim() || null,
          phone: phone?.trim() || null,
          customer_type: customer_type || null,
          external_id: external_id?.trim() || null,
          status: status || "active",
          organization_id: baseUser!.organization_id!,
          metadata:
            Object.keys(metadata).length > 0
              ? (metadata as Record<
                  string,
                  string | number | boolean | null | undefined
                >)
              : undefined,
        });
        closeOnSubmit();
        onInfoShow("Customer created successfully");
        getCustomersByOrganizationId(baseUser!.organization_id!, false);
        // Call callback if provided (e.g., from OrderForm to auto-select new customer)
        if (onCustomerCreated) {
          onCustomerCreated(newCustomer.id);
        }
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.message) {
        const errorMessage = returnErrorMessage(
          Entity.CUSTOMERS,
          error.message
        );
        onErrorShow(errorMessage || "Failed to save customer");
        return;
      }
      onErrorShow("Failed to save customer");
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto", pb: 2 }}>
        <Section>
          <FormInput
            name="name"
            label="Name"
            value={state.name}
            onChange={handleChange}
            errorMessage={state.errors?.name}
            required
          />
          <FormInput
            name="email"
            label="Email"
            value={state.email}
            onChange={handleChange}
            errorMessage={state.errors?.email}
            type="email"
          />
          <FormInput
            name="phone"
            label="Phone"
            value={state.phone}
            onChange={handleChange}
            errorMessage={state.errors?.phone}
          />
          <FormAutocomplete
            label="Customer Type"
            value={state.customer_type || ""}
            onChange={(value: string) => {
              dispatch({
                type: FORM_ACTIONS.SET_FIELD,
                field: "customer_type",
                value: value || null,
              });
              setTouch(true);
            }}
            dropdownPlaceholder="Select customer type..."
            errorMessage={state.errors?.customer_type}
            options={customerTypeOptions}
          />
          <FormAutocomplete
            label="Status"
            value={state.status || "active"}
            onChange={(value: string) => {
              dispatch({
                type: FORM_ACTIONS.SET_FIELD,
                field: "status",
                value: value as "active" | "inactive" | "archived",
              });
              setTouch(true);
            }}
            dropdownPlaceholder="Select status..."
            errorMessage={state.errors?.status}
            options={statusOptions}
          />
          <FormInput
            name="external_id"
            label="External ID"
            value={state.external_id}
            onChange={handleChange}
            errorMessage={state.errors?.external_id}
            placeholder="Optional: ID from external system (POS, ERP, etc.)"
          />
        </Section>

        <Section>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Addresses</Typography>
            <Button
              startIcon={<Add />}
              onClick={handleAddAddress}
              variant="outlined"
              size="small"
            >
              Add Address
            </Button>
          </Box>

          {(state.addresses || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No addresses added. Click "Add Address" to add one.
            </Typography>
          ) : (
            (state.addresses || []).map((address, index) => (
              <Box
                key={address.id}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  p: 2,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2">
                    Address {index + 1}
                    {address.is_default && (
                      <Chip
                        label="Default"
                        size="small"
                        color="primary"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                  <IconButton
                    onClick={() => handleRemoveAddress(address.id)}
                    size="small"
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FormAutocomplete
                    label="Address Type"
                    value={address.type}
                    onChange={(value: string) =>
                      handleAddressChange(
                        address.id,
                        "type",
                        value as AddressType
                      )
                    }
                    dropdownPlaceholder="Select address type..."
                    options={addressTypeOptions}
                  />

                  <FormInput
                    name={`street_${address.id}`}
                    label="Street Address"
                    value={address.street}
                    onChange={(e) =>
                      handleAddressChange(address.id, "street", e.target.value)
                    }
                  />

                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <FormInput
                        name={`city_${address.id}`}
                        label="City"
                        value={address.city}
                        onChange={(e) =>
                          handleAddressChange(
                            address.id,
                            "city",
                            e.target.value
                          )
                        }
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <FormInput
                        name={`state_${address.id}`}
                        label="State/Province"
                        value={address.state}
                        onChange={(e) =>
                          handleAddressChange(
                            address.id,
                            "state",
                            e.target.value
                          )
                        }
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <FormInput
                        name={`postal_code_${address.id}`}
                        label="Postal Code"
                        value={address.postal_code}
                        onChange={(e) =>
                          handleAddressChange(
                            address.id,
                            "postal_code",
                            e.target.value
                          )
                        }
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <FormInput
                        name={`country_${address.id}`}
                        label="Country"
                        value={address.country}
                        onChange={(e) =>
                          handleAddressChange(
                            address.id,
                            "country",
                            e.target.value
                          )
                        }
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <input
                      type="checkbox"
                      checked={address.is_default}
                      onChange={(e) =>
                        handleAddressChange(
                          address.id,
                          "is_default",
                          e.target.checked
                        )
                      }
                      style={{ marginRight: 8 }}
                    />
                    <Typography variant="body2">
                      Set as default address
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Section>
      </Box>

      <Footer
        handleClose={handleClose}
        handleSubmit={
          createFromOrderView ? handleCreateFromOrderWindow : handleSubmit
        }
      />
    </Box>
  );
};
